.textField {
    --padding: var(--spacer-16);

    display: flex;
    flex-direction: column;
    gap: var(--spacer-8);

    &[data-disabled] .textFieldWrapper {
        opacity: 0.5;
    }
}

:global(.react-aria-FieldError) {
    color: var(--color-invalid, var(--red));
    font: var(--font-p-14);
}

.textFieldWrapper {
    position: relative;
    border-bottom: 1px solid var(--color-text);

    &:has([data-invalid="true"]) {
        border-bottom-color: var(--color-invalid, var(--red));
    }

    input,
    .selectButton {
        background: transparent;
        color: inherit;
        font: var(--font-p-16);
        border: none;
        width: 100%;
        height: 100%;
        padding-block: var(--padding) var(--spacer-8);

        &:focus-visible {
            outline: none;
        }
    }

    &:has(input:is(:focus, [data-focused="true"])) {
        outline-offset: 1px;
        outline: 1px solid var(--color-contrast);
    }

    &:has(
        input:is(
            :focus:not(:focus-visible),
            [data-focused="true"]:not([data-focus-visible="true"])
        )
    ) {
        outline: none;
    }
}

.textFieldLabel {
    composes: fontB12 from "../../styles/typography.module.scss";
    position: absolute;
    left: 0;
    top: var(--padding);
    color: var(--color-text);

    // slide on out of the way
    transition:
        translate 0.1s,
        font 0.1s;
    translate: 0 calc(-1 * var(--padding));

    /*  move label out of the way of text if:
            1. input (any) is focused
            2. input (text or text area) is not empty
            3. is labeling a dropdown (those always have placeholders)
        */
    &:not(
        :has(
            + :focus,
            + [data-focused="true"],
            + input:not([value=""]),
            + textarea:not(.empty)
        )
    ) {
        translate: var(--label-translate-placeholder, 0);
        text-transform: none;
        font: var(--label-font-placeholder, var(--font-st-16));
        color: var(--dark-gray);
    }

    &:has(+ [required])::after {
        content: "*";
    }
}

.childWrapper {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
}

.groupLabel {
    composes: fontB16 from "../../styles/typography.module.scss";
    font-weight: bold;
}

// Used to implement both checkbox and radio
.circlebox {
    --size: 20px;

    display: inline-block;
    width: var(--size);
    min-width: var(--size); // preserves width in flex boxes
    height: var(--size);
    border-radius: var(--border-radius-full);
    border: 1px solid var(--black);

    [data-hovered="true"] &,
    [data-selected="true"] & {
        border-color: var(--red);
    }
}

.checkbox {
    composes: circlebox;
    padding: 2px;

    [data-selected="true"] & {
        background-color: var(--red);
        color: var(--white);

        svg {
            opacity: 1;
        }
    }

    svg {
        opacity: 0;
        width: 100%;
    }
}

.radio {
    composes: circlebox;
    position: relative;

    [data-selected="true"] &::after {
        background-color: var(--red);
    }

    &::after {
        position: absolute;
        inset: 3px;
        border-radius: var(--border-radius-full);
        content: "";
    }
}

.fieldWrapper {
    composes: fontST16 from "../../styles/typography.module.scss";
    display: inline-flex;
    align-items: center;
    gap: var(--spacer-8);

    &[data-focus-visible="true"] {
        outline: 1px solid black;
        outline-offset: 1px;
    }

    &[data-disabled="true"] {
        opacity: 0.5;
    }
}

.fieldGroup {
    display: flex;
    flex-direction: column;
    gap: var(--spacer-16);

    & + & {
        margin-top: var(--spacer-32);
    }
}

.selectButton {
    composes: base from "../Clickables/index.module.scss";
    justify-content: space-between;

    & svg {
        rotate: 90deg;
    }

    &[aria-expanded="true"] svg {
        rotate: -90deg;
    }
}

.selectValue {
    align-self: flex-start;

    &[data-placeholder] {
        opacity: 0;
    }
}

.dropdown {
    width: var(--trigger-width);
    padding: var(--spacer-16);
    box-shadow: var(--box-shadow-2);
    background: var(--color-primary);
}

.selectWrapper {
    composes: textFieldWrapper;

    .label {
        position: absolute;
        left: 0;
        top: var(--padding);
        color: var(--color-text);
        font: var(--font-button-12);

        // slide on out of the way
        transition:
            translate 0.1s,
            font 0.1s;
        translate: 0 calc(-1 * var(--padding));

        /*  move label out of the way of text if:
            1. input (any) is focused
            2. input (text or text area) is not empty
            3. is labeling a dropdown (those always have placeholders)
        */
        &:not(
            :has(
                + [aria-haspopup="listbox"]
                    > .selectValue:not([data-placeholder="true"])
            )
        ) {
            translate: 0;
            text-transform: none;
            font: var(--font-st-16);
            color: var(--dark-gray);
        }

        &:has(+ [required])::after {
            content: "*";
        }
    }

    &:has([data-focused="true"]) {
        outline-offset: 1px;
        outline: 1px solid var(--color-contrast);
    }

    &:has([data-focused="true"]:not([data-focus-visible="true"])) {
        outline: none;
    }
}

.dropdownItems {
    display: flex;
    flex-direction: column;

    &[data-focused]:not([data-focus-visible]) {
        outline: none;
    }

    & > * {
        padding-block: calc(var(--spacer-16) / 2);
    }
}

.dropdownItem {
    composes: fontST16 from "../../styles/typography.module.scss";
    cursor: pointer;

    &[data-selected] {
        color: var(--color-contrast);
    }

    &[data-hovered] {
        background: var(--transparent-20);
    }

    &[data-focused]:not([data-focus-visible]) {
        outline: none;
    }
}
